/**
 * @author: lv.chen3
 * @desc: 切换下拉筛选器
 * @date: 2025/3/23
 */
import React, { useRef, useState } from 'react';
import classnames from 'classnames';
import { Dropdown, DropdownProps, SearchBox } from 'tea-component';
import SvgIcon from '@home/components/SvgIcon';
import Loading from '@gp/components/Loading';
import NoData from '@gp/components/NoData';
import NewFeatureTipsBubble from '@gp/components/NewFeatureTipsBubble';
import ListPanel from './components/ListPanel';
import { selectFilter, reportBtnOperationLog } from '@gp/utils/common';
import { CONSTANT_TEXTS, GET_TEXT_WITH_HIGHLIGHT_OPTION } from '@gp/constants/const';

import type { IBaseProps } from '@home/types/common';
import type { IProjectDTO } from '@gp/types/projectDetail';

import './index.scss';

interface IProjectSwitcherProps extends IBaseProps, Omit<DropdownProps, 'button' | 'children'> {
  projectsLoading?: boolean;
  projects?: IProjectDTO[] | null;
  projectsRecordsLoading?: boolean;
  projectsRecords?: any;
  onPushProjectsRecord?: (data?: IProjectDTO | null) => void;
  name?: string;
  value?: string;
  onChange?: (projectId?: string, project?: IProjectDTO | null) => void;
}

const formatOptions = (projects?: IProjectDTO[] | null): IGPSelectOptionWithGroup[] => {
  if (!Array.isArray(projects) || projects.length === 0) return [];
  return projects.map((project) => {
    const { project_id: projectId, project: projectName, cover: projectCover } = project || {};
    return {
      value: projectId || '',
      text: projectName || '',
      label: projectName || '',
      icon: projectCover || '',
    };
  });
};

const filterOptions = (options?: IGPSelectOptionWithGroup[], searchValue?: string): IGPSelectOptionWithGroup[] => {
  if (!Array.isArray(options) || options.length === 0) return [];
  if (!searchValue) return options;

  return options.filter((option) => {
    return selectFilter(option, searchValue, GET_TEXT_WITH_HIGHLIGHT_OPTION);
  });
};

const formatOptionsByRecords = (loading?: boolean, records?: any): IGPSelectOptionWithGroup[] => {
  if (!!loading) return [];
  if (!Array.isArray(records) || records.length === 0) return [];
  return records.map((record) => {
    const { project_id: projectId, name: projectName, img: projectCover } = record || {};
    return {
      value: projectId || '',
      text: projectName || '',
      label: projectName || '',
      icon: projectCover || '',
    };
  });
};

const renderButton = (name?: string): React.ReactElement => {
  return (
    <div className="dbg-pipeline-project-switcher-button">
      <span className="dbg-pipeline-project-switcher-button__text">{name || ''}</span>
      <NewFeatureTipsBubble
        placement="bottom"
        newFeatureTipsKey="dbg-pipeline-project-details-project-switcher-tips"
        newFeatureTipsContent="Click here to view another project."
      >
        <SvgIcon className="dbg-pipeline-project-switcher-button__icon-switch" type="switch" />
      </NewFeatureTipsBubble>
    </div>
  );
};

const ProjectSwitcher: React.FC<IProjectSwitcherProps> = (props) => {
  const {
    className,
    overlayClassName,
    boxClassName,
    projectsLoading,
    projects,
    projectsRecordsLoading,
    projectsRecords,
    onPushProjectsRecord,
    name,
    value,
    onChange,
    ...restProps
  } = props;

  const [searchValue, setSearchValue] = useState('');

  const loading = projectsRecordsLoading || projectsLoading;

  const projectsRecordsOptions = formatOptionsByRecords(projectsRecordsLoading, projectsRecords);

  const options = formatOptions(projects);

  const optionsFiltered = filterOptions(options, searchValue);

  const isSearchEmpty = !optionsFiltered?.length;

  const button = renderButton(name);

  const scroller = useRef<HTMLDivElement>(null);

  const handleSearch = (payload?: string) => {
    setSearchValue(payload || '');
  };

  const handleSelect = (payload?: string, project?: IProjectDTO | null) => {
    reportBtnOperationLog('DETAIL_PROJECT_DETAIL_SWITCH_PROJECT_SUBMIT', {
      extInfo: `switch project from ${value} to ${payload}`,
    });
    onChange?.(payload || '', project);
  };

  const handleOpenChange = (open?: boolean) => {
    if (!open) {
      setTimeout(() => {
        scroller.current?.scrollTo({ top: 0, behavior: 'auto' });
      });
    }
  };

  return (
    <Dropdown
      {...restProps}
      className={classnames('dbg-pipeline-project-switcher dbg-initial-line-height', className)}
      overlayClassName={classnames('dbg-pipeline-project-switcher-overlay', overlayClassName)}
      boxClassName={classnames('dbg-pipeline-project-switcher-box', boxClassName)}
      appearance="pure"
      clickClose={false}
      destroyOnClose={false}
      button={button}
      onOpenChange={handleOpenChange}
    >
      {(close) => {
        const handlePlus = (payload?: string) => {
          const project = projects?.find((item) => item?.project_id === payload);
          handleSelect(payload, project);
          onPushProjectsRecord?.(project);
          close?.();
        };

        if (loading) return <Loading style={{ height: 200 }} loading />;
        return (
          <div className="dbg-pipeline-project-switcher-box-body">
            <SearchBox
              className="dbg-pipeline-project-switcher-box-body__search-box"
              placeholder="Search"
              value={searchValue}
              onChange={handleSearch}
              onSearch={handleSearch}
              onClear={() => handleSearch('')}
            />
            <div ref={scroller} className="dbg-pipeline-project-switcher-box-body__scroller">
              <div className="dbg-pipeline-project-switcher-box-body__scroller-inner">
                <ListPanel
                  className={classnames({ hide: !!searchValue || !projectsRecordsOptions?.length })}
                  title="Recently Viewed"
                  currentValue={value}
                  options={projectsRecordsOptions}
                  onSelect={handlePlus}
                />
                {isSearchEmpty && <NoData height={70} text={CONSTANT_TEXTS.EMPTY_TEXT1} />}
                <ListPanel
                  className={classnames({ hide: isSearchEmpty })}
                  title="All Project"
                  searchValue={searchValue}
                  currentValue={value}
                  options={isSearchEmpty ? options : optionsFiltered}
                  onSelect={handlePlus}
                />
              </div>
            </div>
          </div>
        );
      }}
    </Dropdown>
  );
};

export default ProjectSwitcher;
